import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

const STORAGE_KEY = 'profile:middleCollapsed';

/**
 * Whether the profile's middle column (statistics) is collapsed. Remembered
 * per browser; works without storage too. `defaultCollapsed` is the state
 * before anyone opened or closed it in this browser.
 */
export function useMiddleCollapsed({ defaultCollapsed = false, storageKey = STORAGE_KEY } = {}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? defaultCollapsed : stored === '1';
    } catch {
      return defaultCollapsed;
    }
  });
  const set = (value) => {
    setCollapsed(value);
    try {
      localStorage.setItem(storageKey, value ? '1' : '0');
    } catch {
      // private mode: just not remembered
    }
  };
  return [collapsed, set];
}

/** Round button in the middle column's corner that collapses it. */
export function MiddleCollapseButton({ onCollapse }) {
  const { t } = useTranslation('translations');
  const label = t('employeeProfile.hideMiddle');
  return (
    <Tooltip title={label} placement="left">
      <button
        type="button"
        aria-label={label}
        data-tour="profile-collapse"
        onClick={onCollapse}
        className="absolute right-2 top-2 z-[2] grid h-7 w-7 cursor-pointer place-items-center rounded-full border border-[rgba(var(--accentColor),0.45)] bg-white text-xs text-accent shadow-[0_1px_4px_rgba(16,24,40,0.12)] transition-colors hover:bg-[rgba(var(--accentColor),0.08)]"
      >
        <LeftOutlined />
      </button>
    </Tooltip>
  );
}

/** What is left of a collapsed middle column: a thick bar that reopens it. */
export function MiddleCollapsedBar({ onExpand }) {
  const { t } = useTranslation('translations');
  const label = t('employeeProfile.showMiddle');
  return (
    <Tooltip title={label} placement="right">
      <button
        type="button"
        aria-label={label}
        data-tour="profile-collapse"
        onClick={onExpand}
        className="group relative h-full w-full cursor-pointer rounded-full bg-white/35 transition-colors hover:bg-white/55"
      >
        {/* Always visible, so it is clear where the column comes back from. */}
        <span className="absolute left-1/2 top-0 grid h-7 w-7 -translate-x-1/2 place-items-center rounded-full border border-[rgba(var(--accentColor),0.45)] bg-white text-xs text-accent shadow-[0_1px_4px_rgba(16,24,40,0.15)] transition-colors group-hover:bg-zinc-50">
          <RightOutlined />
        </span>
      </button>
    </Tooltip>
  );
}
